import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Bilingual postProcessor (Genius One Plus).
// Fires ONLY for the two bilingual locales — `si-en-dual` and `ta-en-dual` —
// added for the English-aspiring Sinhalese middle-class audience and for
// Chennai/Coimbatore + urban SL Tamil parents respectively. Every standalone
// locale (en / si / ta) renders as-is. The two `*-en-dual` resources are
// aliased to their native counterparts below, so the raw `value` here is
// already the native string — we just fetch the English string for the same
// key and concatenate.
const BILINGUAL_LOCALES = new Set(['si-en-dual', 'ta-en-dual']);
const bilingualPostProcessor = {
  type: 'postProcessor',
  name: 'bilingual',
  process: function(value, key, options, translator) {
    const currentLang = translator.language || i18n.language;
    if (!BILINGUAL_LOCALES.has(currentLang)) return value;
    const englishValue = i18n.t(key, { ...options, lng: 'en', postProcess: [] });
    if (value === englishValue) return value;
    return `${value} / ${englishValue}`;
  }
};

// Shared keys across all languages - define English first, then each language
const en = {
  "lessons.en_only_notice": 'Lessons are delivered in English — your progress and support are in your language.',
  // Navigation
  "home": "Home",
  "dashboard": "Dashboard",
  "activities": "Activities",
  "progress": "Progress",
  "logout": "Logout",
  "login": "Login",
  "register": "Register",
  
  // Landing Page — Week 2 Genius One Plus copy
  "hero.title": "Genius One Plus",
  "hero.tagline": "A Grade 6 Transition Programme built for Sri Lankan families",
  "hero.subtitle": "Prepare your child for Grade 6 with Maths, English, Science, and Basic Computing — in your language, on your phone.",
  "hero.cta": "Start Free Trial",
  "hero.cta.secondary": "See a Sample Lesson",
  "hero.trial.badge": "Free 7-day trial. No card required.",

  // Sprint 10.6.6 — /try page UI chrome (English)
  // NOTE: Lesson content (title, hook, steps, wow_moment, share_prompt) is
  // ALWAYS English regardless of UI language. This block is for UI chrome ONLY.
  "try.badge.free": "🎁 Free · No signup · 15 minutes",
  "try.audio.listen": "🔊 Listen to the intro",
  "try.audio.playing": "⏸ Playing…",
  "try.share.friend": "💬 Share with a friend",
  "try.wow.title": "✨ The wow moment",
  "try.steps.heading": "Steps ({{count}})",
  "try.complete.btn": "🐱 I made the cat talk! →",
  "try.nudge.title": "🎉 You made a talking cat!",
  "try.nudge.subtitle": "Show your amma — then unlock 40 more.",
  "try.nudge.show": "Show your amma 📱",
  "try.nudge.unlock": "Unlock 40 more →",
  "try.share.prompt.title": "📸 Show it off",
  "try.cta.blurb": "40 more lessons · Maths · English · Science · Scratch · Certificates",
  "try.cta.register": "Register for LKR {{price}}/month →",
  "try.error": "Could not load the sample lesson",
  "try.loading": "Loading…",
  "try.lesson.en_only_hint": "Lesson audio & steps are in English — your buttons and labels follow your language.",

  // Feature cards — the four Grade 6 subjects
  "subjects.section.title": "Four subjects. One transition. Twelve weeks.",
  "subjects.maths.title": "Mathematics",
  "subjects.maths.subtitle": "Master Grade 6 Maths from Day 1",
  "subjects.maths.body": "From fractions to geometry — build the foundations your child needs for Grade 6 success. Weekly practice questions, monthly progress tracking, results in 15 minutes.",
  "subjects.english.title": "English",
  "subjects.english.subtitle": "Read, Write, and Speak with Confidence",
  "subjects.english.body": "Grade 6 introduces reading comprehension, essays, and grammar. Our lessons prepare your child to enter Grade 6 English at grade level from day one.",
  "subjects.science.title": "Science",
  "subjects.science.subtitle": "Grade 6 Science Made Simple",
  "subjects.science.body": "Biology, physics, chemistry — three sciences your child meets for the first time in Grade 6. Start with the basics now, walk into Grade 6 already knowing the vocabulary.",
  "subjects.scratch.title": "Basic Computing with Scratch",
  "subjects.scratch.subtitle": "Your Child's First Computer Program",
  "subjects.scratch.body": "The world's most popular programming tool for kids. Runs on any smartphone or tablet — no laptop needed. Your child builds real games and animations while learning to think like a computer scientist.",
  "subjects.scratch.badge": "NEW",

  // Pricing — LKR 750/mo · LKR 2000 for the 3-month programme (pricing pivot w4)
  "pricing.section.title": "Simple pricing. Free 7-day trial.",
  "pricing.monthly.title": "Monthly",
  "pricing.monthly.price": "LKR 750",
  "pricing.monthly.unit": "/ month per student",
  "pricing.monthly.includes": "All 4 subjects · monthly progress report · WhatsApp / social share",
  "pricing.package.title": "Full Programme",
  "pricing.package.recommended": "Recommended",
  "pricing.package.price": "LKR 2,000",
  "pricing.package.unit": "for the full 3-month programme (October–December)",
  "pricing.package.save": "Save LKR 250 vs monthly",
  "pricing.package.includes": "All 4 subjects · monthly progress report · WhatsApp / social share",
  "pricing.cta.trial": "Start Free Trial",

  "features.algorithms": "Algorithms",
  "features.algorithms.desc": "Sorting, searching, and problem-solving through physical activities",
  "features.ai": "AI & ML",
  "features.ai.desc": "Pattern recognition, decision trees, and neural networks",
  "features.data": "Data & Logic",
  "features.data.desc": "Binary, logic gates, encryption, and compression",
  "pricing.simple": "Simple Pricing",
  
  // Dashboard
  "welcome": "Welcome",
  "ready": "Ready to learn something new?",
  "stats.activities": "Activities",
  "stats.points": "Points",
  "stats.time": "Time Spent",
  "stats.badges": "Badges",
  "congrats": "Congratulations!",
  "certificate.message": "You've completed {{count}} activities! Download your certificate.",
  "certificate.download": "Download Certificate",
  
  // Activities
  "activity.start": "Start Activity",
  "activity.complete": "Mark as Complete",
  "activity.instructions": "Instructions",
  "activity.objectives": "Learning Objectives",
  "activity.completed": "Activity Completed!",
  "activity.back": "Back to Activities",
  "filter.all": "All",
  "filter.mylevel": "My Level",
  
  // Auth
  "auth.create": "Create Account",
  "auth.welcome": "Welcome Back",
  "auth.fullname": "Full Name",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.role": "I am a",
  "auth.student": "Student",
  "auth.parent": "Parent",
  "auth.teacher": "Teacher",
  "auth.agelevel": "Age Level",
  "auth.foundation": "Foundation (5-8 years)",
  "auth.development": "Development (9-12 years)",
  "auth.mastery": "Mastery (13-16 years)",
  "auth.loginsubtitle": "Login to continue",
  "auth.startjourney": "Start your learning journey",
  "auth.noaccount": "Don't have an account?",
  "auth.haveaccount": "Already have an account?",
  
  // Voice
  "voice.play": "Listen",
  "voice.stop": "Stop",
  "voice.language": "Language",
  
  // Pricing & Payment
  "pricing.title": "Choose Your Plan",
  "pricing.subtitle": "Unlock all premium activities and features",
  "pricing.subscribe": "Subscribe Now",
  "pricing.getstarted": "Get Started",
  "pricing.back": "Back",
  "pricing.foundation": "Foundation Level",
  "pricing.development": "Development Level",
  "pricing.mastery": "Mastery Level",
  "pricing.permonth": "/month",
  "pricing.methods": "Available Payment Methods",
  "pricing.whatsincluded": "What's Included in Premium?",
  "pricing.premiumactivities": "15 Premium Activities",
  "pricing.premiumactivities.desc": "Advanced concepts and challenges",
  "pricing.alllanguages": "All 13 Languages",
  "pricing.alllanguages.desc": "Learn in your native language",
  "pricing.voiceexplanations": "Voice Explanations",
  "pricing.voiceexplanations.desc": "Listen to content in any language",
  "pricing.certificates": "Certificates",
  "pricing.certificates.desc": "Download achievement certificates",
  
  // Payment Dialog
  "payment.select": "Select Payment Method",
  "payment.qr": "Bank Transfer (QR Code)",
  "payment.qr.desc": "Scan QR code with your banking app",
  "payment.card": "Card Payment (Visa / MasterCard)",
  "payment.card.desc": "Pay securely with your credit or debit card",
  "payment.paypal": "PayPal",
  "payment.paypal.desc": "Pay with your PayPal account or card via PayPal",
  "payment.amount": "Amount to pay",
  "payment.monthly": "Monthly",
  "payment.instructions": "Instructions",
  "payment.step1": "Open your banking app",
  "payment.step2": "Scan the QR code above",
  "payment.step3": "Verify the amount",
  "payment.step4": "Complete the transfer",
  "payment.step5": "Click \"I've Made the Payment\" below",
  "payment.done": "I've Made the Payment",
  "payment.notifying": "Notifying...",
  "payment.verification": "Your subscription will be activated within 24 hours after payment verification.",
  "payment.cardbrands": "Secure payment powered by Stripe",
  "payment.redirect": "You'll be redirected to our secure payment processor to complete your payment.",
  "payment.paywithcard": "Pay with Card",
  "payment.paywithpaypal": "Pay with PayPal",
  "payment.paypalredirect": "Pay securely using your PayPal account or any card through PayPal.",
  "payment.paypalredirectmsg": "You'll be redirected to PayPal to complete your payment securely.",
  "payment.backtomethods": "Back to payment methods",
  "payment.banktransfer": "Bank Transfer via QR Code",
  "payment.cardpayment": "Card Payment",
  "payment.paypalpayment": "PayPal Payment",
  "payment.bankqr": "Bank Transfer (QR)",
  "payment.cardmethod": "Card Payment",
  
  // Subscription
  "subscription.activating": "Activating Subscription...",
  "subscription.wait": "Please wait while we confirm your payment",
  "subscription.success": "Payment Successful!",
  "subscription.active": "Your premium subscription is now active. You have access to all premium activities!",
  "subscription.gotodashboard": "Go to Dashboard",
  
  // General
  "loading": "Loading...",
  "back": "Back",
  "free": "Free",
  "premium": "Premium",
  "minutes": "min",

  // ---- Genius One Plus registration form (Week 3) ----
  "reg.title": "Start Your Free 7-Day Trial",
  "reg.subtitle": "No card required. First lesson sent on WhatsApp today.",
  "reg.parent.name": "Parent's Name",
  "reg.parent.whatsapp": "Parent's WhatsApp Number",
  "reg.parent.whatsapp.hint": "9 digits after +94 (e.g. 771234567)",
  "reg.student.name": "Student's Name",
  "reg.student.grade": "Student's Current Grade",
  "reg.student.grade.g5": "Grade 5 (Preparing for Grade 6)",
  "reg.student.grade.g6": "Grade 6 (Just started, need reinforcement)",
  "reg.student.school": "Student's School (optional)",
  "reg.student.district": "District / City (optional)",
  "reg.language": "Language of Instruction",
  "reg.language.si": "Sinhala",
  "reg.language.ta": "Tamil",
  "reg.language.en": "English",
  "reg.contact": "Preferred contact method",
  "reg.contact.whatsapp": "WhatsApp (recommended)",
  "reg.contact.phone": "Phone call",
  "reg.contact.email": "Email",
  "reg.email": "Email (optional)",
  "reg.submit": "Start Free Trial",
  "reg.submitting": "Creating your trial…",
  "reg.success": "Welcome! Your 7-day free trial has started. We'll WhatsApp you the first lesson today.",
  "reg.error.generic": "Something went wrong. Please try again or WhatsApp us on +94 77 123 4567.",
  "reg.error.duplicate": "This WhatsApp number is already registered. Try logging in instead.",
  "reg.error.district": "Please select a district from the list.",
  "reg.student.district.placeholder": "— Select district —",
  "reg.done": "Done",
  "reg.id.label": "Registration ID",
  "reg.status.label": "Status",
  "reg.paid.until.label": "Free access until",
  "reg.free.title": "You are IN for FREE this October!",
  "reg.free.subtitle": "Your first month is on us. No payment needed until 01 November.",
  "reg.free.body": "We will WhatsApp you the lesson link every morning at 6:00 SLT. Save our number so it does not go to spam.",
  "reg.free.wa.cta": "Send WhatsApp reminder",
  "reg.free.wa.text": "Hi, I registered for Genius One Plus (Free October). My ID is:",
  "reg.pay.title": "One last step — Bank of Ceylon transfer",
  "reg.pay.subtitle": "Your seat is held. Complete the transfer and upload the receipt.",
  "reg.pay.bank": "Bank",
  "reg.pay.account.name": "Account name",
  "reg.pay.account.number": "Account number",
  "reg.pay.branch": "Branch",
  "reg.pay.wa.number": "WhatsApp receipt to",
  "reg.pay.ref.label": "Reference",
  "reg.pay.plan": "Choose plan",
  "reg.pay.plan.1mo": "Monthly",
  "reg.pay.plan.3mo": "3-month",
  "reg.pay.qr.hint": "Scan with your bank app",
  "reg.pay.receipt": "Upload receipt (JPG / PNG)",
  "reg.pay.receipt.required": "Please attach the receipt image",
  "reg.pay.receipt.ok": "Receipt received — we will confirm within 24 hours",
  "reg.pay.receipt.error": "Upload failed, please try again",
  "reg.pay.submit": "Submit receipt",
  "reg.pay.submitting": "Uploading...",
  "reg.pay.load.error": "Could not load payment details. Please refresh.",
  "reg.pay.twl.reassurance": "TEC Sri Lanka Worldwide is our parent company. Genius One Plus is one of its programmes. Your bank receipt will show \"TEC SRI LANKA WORLDWIDE (PVT) LTD\" as the payee — this is us.",

  // Sprint 5 — Lessons / Quiz / Progress
  "common.loading": "Loading…",
  "lessons.title": "Lessons",
  "lessons.subtitle": "Pick a lesson. Complete the quiz. Watch your progress grow.",
  "lessons.filter.all": "All",
  "lessons.filter.maths": "Maths",
  "lessons.filter.english": "English",
  "lessons.filter.science": "Science",
  "lessons.progress.cta": "My Progress →",
  "lessons.start": "Start lesson",
  "lessons.retry": "Retry quiz",
  "lessons.done": "Done",
  "lessons.qcount": "{{count}} questions",
  "quiz.submit": "Submit quiz",
  "quiz.submitting": "Submitting…",
  "quiz.correct": "correct",
  "quiz.your_answer": "your answer",
  "quiz.explanation": "Why:",
  "quiz.result.line": "{{correct}} of {{total}} correct",
  "quiz.badges.earned": "New badges earned:",
  "quiz.back": "Back to lessons",
  "quiz.see_progress": "See my progress →",
  "quiz.not_found": "Lesson not found.",
  "quiz.progress": "{{done}} / {{total}}",
  "progress.hello": "Hello,",
  "progress.stat.lessons": "Lessons done",
  "progress.stat.avg": "Avg score",
  "progress.stat.streak": "Streak",
  "progress.stat.top": "Top 10%",
  "progress.subjects": "Subjects",
  "progress.subject.maths": "Maths",
  "progress.subject.english": "English",
  "progress.subject.science": "Science",
  "progress.subject.completed_of": "{{done}} of {{total}} lessons",
  "progress.subject.avg": "Avg:",
  "progress.badges": "Badges",
  "progress.badges.empty": "No badges yet — complete a quiz to unlock your first one.",
  "progress.cta": "Take another lesson →",
  "progress.not_found": "Registration not found.",

  // Sprint 5 — hero illustration + story strip
  "hero.illustration.caption": "Every child begins with a question. Genius One Plus turns questions into skills.",
  "story.section.title": "A programme built around real childhood moments",
  "story.card.one": "When a child dreams big, they need somewhere to point that curiosity.",
  "story.card.two": "AI is not scary when you meet it the right way — through a fingertip and a smile.",
  "story.card.three": "Every kid learns better when a friend cheers them on.",

  // Sprint 6/7 — marketing gallery + TN preview
  "gallery.section.title": "Our vision, painted",
  "gallery.01": "Dream big under one sky",
  "gallery.02": "Every child has a universe to explore",
  "gallery.03": "AI is a tool. Kids are the makers.",
  "gallery.04": "Learn together, grow together",
  "gallery.05": "She built it. So can you.",
  "gallery.06": "For every amma. For every aachchi.",
  "gallery.07": "Family is the first classroom",
  "gallery.08": "Tomorrow's tools, today's kids",
  "gallery.09": "Amma knows best. Amma helps.",
  "gallery.10": "Every family. Every certificate. Every celebration.",
  "gallery.11": "My child just built something.",
  "tn.badge": "Coming Q2 2027",
  // Sprint 10.9 — Unified waitlist ETA badge shared across MY, IN-Hi, ID, TH, PK, HK.
  "waitlist.eta.coming_2027": "Coming 2027",
  "tn.hero.title": "Genius One Plus is coming to Tamil Nadu",
  "tn.hero.sub": "Chennai • Coimbatore • Madurai — Genius One Plus is coming",
  "tn.waitlist.title": "Notify me when we launch in Tamil Nadu",
  "tn.waitlist.cta": "Notify me",
  "tn.waitlist.done": "You are on the list. We will WhatsApp you when we launch.",
  "tn.pricing.tba": "INR pricing to be announced closer to launch. No purchases yet.",
  "reg.error.whatsapp": "Please enter a valid 10-digit WhatsApp number (after +94).",
  "reg.have.account": "Already have an account?",
  "reg.login.link": "Log in"
};

// Tamil
// Translation register (Week 3): NEUTRAL FORMAL WRITTEN TAMIL (Chennai /
// textbook register). Serves both Sri Lankan Tamil and Tamil Nadu audiences
// so the same copy can be reused when Genius One Plus expands to TN in 2027.
// Rules applied:
//   • "வகுப்பு" for grade (NOT SL-Tamil "தரம்")
//   • "பள்ளி" for school, "மாணவர்" for student, "பெற்றோர்" for parent
//   • No SL-Tamil slang (avoid "தம்பி" as a generic address, etc.)
//   • No SL-specific exam references (say "தேசிய தேர்வு" if needed, not
//     "5-ம் வகுப்பு புலமைப்பரிசில் தேர்வு")
// The `இலங்கை` mentions in the tagline / +94 hints are launch-market copy,
// not dialect — those stay until we open a TN storefront.
const ta = {
  "lessons.en_only_notice": 'பாடங்கள் ஆங்கிலத்தில் வழங்கப்படுகின்றன — உங்கள் முன்னேற்றமும் உதவியும் உங்கள் மொழியில்.',
  "reg.pay.twl.reassurance": "TEC Sri Lanka Worldwide எங்கள் தாய் நிறுவனம். Genius One Plus அதன் திட்டங்களில் ஒன்று. உங்கள் வங்கி ரசீதில் \"TEC SRI LANKA WORLDWIDE (PVT) LTD\" என்று செலுத்துபவராகக் காட்டப்படும் — இது எங்களுடையது.",
  "home": "முகப்பு",
  "dashboard": "டாஷ்போர்டு",
  "activities": "செயல்பாடுகள்",
  "progress": "முன்னேற்றம்",
  "logout": "வெளியேறு",
  "login": "உள்நுழைய",
  "register": "பதிவு செய்க",
  "hero.title": "ஜீனியஸ் ஒன் பிளஸ்",
  /* TODO_HUMAN_REVIEW */ "hero.subtitle": "6-ம் வகுப்பிற்கான தயாரிப்பு: கணிதம், ஆங்கிலம், அறிவியல் மற்றும் அடிப்படை கணினி — உங்கள் மொழியில், உங்கள் தொலைபேசியில்.",
  /* TODO_HUMAN_REVIEW */ "hero.cta": "இலவச சோதனையைத் தொடங்கவும்",
  /* TODO_HUMAN_REVIEW */ "hero.cta.secondary": "மாதிரி பாடத்தைப் பார்க்கவும்",
  /* TODO_HUMAN_REVIEW */ "hero.trial.badge": "7 நாள் இலவச சோதனை. அட்டை தேவையில்லை.",
  /* TODO_HUMAN_REVIEW */ "hero.tagline": "இலங்கை குடும்பங்களுக்கான வகுப்பு 6 மாற்றத் திட்டம்",
  /* TODO_HUMAN_REVIEW */ "subjects.section.title": "நான்கு பாடங்கள். ஒரு மாற்றம். பன்னிரண்டு வாரங்கள்.",
  /* TODO_HUMAN_REVIEW */ "subjects.maths.title": "கணிதம்",
  /* TODO_HUMAN_REVIEW */ "subjects.maths.subtitle": "முதல் நாளிலிருந்தே 6-ம் வகுப்பு கணிதத்தில் தேர்ச்சி",
  /* TODO_HUMAN_REVIEW */ "subjects.maths.body": "பின்னங்கள் முதல் வடிவவியல் வரை — உங்கள் குழந்தையின் 6-ம் வகுப்பு வெற்றிக்கான அடித்தளத்தை உருவாக்குங்கள். வாராந்திர பயிற்சி கேள்விகள், மாதாந்திர முன்னேற்ற கண்காணிப்பு, 15 நிமிடங்களில் முடிவுகள்.",
  /* TODO_HUMAN_REVIEW */ "subjects.english.title": "ஆங்கிலம்",
  /* TODO_HUMAN_REVIEW */ "subjects.english.subtitle": "நம்பிக்கையுடன் படிக்க, எழுத, பேசுங்கள்",
  /* TODO_HUMAN_REVIEW */ "subjects.english.body": "6-ம் வகுப்பில் வாசிப்பு புரிதல், கட்டுரைகள் மற்றும் இலக்கணம் அறிமுகமாகின்றன. முதல் நாளிலிருந்தே உங்கள் குழந்தை 6-ம் வகுப்பு ஆங்கிலத்தில் தரத்திற்கு ஏற்ப தயாராக இருக்கும்.",
  /* TODO_HUMAN_REVIEW */ "subjects.science.title": "அறிவியல்",
  /* TODO_HUMAN_REVIEW */ "subjects.science.subtitle": "6-ம் வகுப்பு அறிவியல் எளிதாக்கப்பட்டது",
  /* TODO_HUMAN_REVIEW */ "subjects.science.body": "உயிரியல், இயற்பியல், வேதியியல் — 6-ம் வகுப்பில் உங்கள் குழந்தை முதன்முறையாக சந்திக்கும் மூன்று அறிவியல்கள். இப்போது அடிப்படைகளிலிருந்து தொடங்கி, சொற்களஞ்சியத்தை ஏற்கனவே அறிந்திருக்கும் நிலையில் 6-ம் வகுப்பில் நுழையுங்கள்.",
  /* TODO_HUMAN_REVIEW */ "subjects.scratch.title": "Scratch கொண்டு அடிப்படை கணினி",
  /* TODO_HUMAN_REVIEW */ "subjects.scratch.subtitle": "உங்கள் குழந்தையின் முதல் கணினி நிரல்",
  /* TODO_HUMAN_REVIEW */ "subjects.scratch.body": "குழந்தைகளுக்கான உலகின் மிகவும் பிரபலமான நிரலாக்க கருவி. எந்த ஸ்மார்ட்போன் அல்லது டேப்லெட்டிலும் இயங்கும் — லேப்டாப் தேவையில்லை. ஒரு கணினி விஞ்ஞானியைப் போல சிந்திக்க கற்றுக்கொள்ளும்போது உங்கள் குழந்தை உண்மையான விளையாட்டுகள் மற்றும் அனிமேஷன்களை உருவாக்குகிறது.",
  /* TODO_HUMAN_REVIEW */ "subjects.scratch.badge": "புதியது",
  /* TODO_HUMAN_REVIEW */ "pricing.section.title": "எளிய விலை நிர்ணயம். 7 நாள் இலவச சோதனை.",
  /* TODO_HUMAN_REVIEW */ "pricing.monthly.title": "மாதாந்திரம்",
  "pricing.monthly.price": "LKR 750",
  /* TODO_HUMAN_REVIEW */ "pricing.monthly.unit": "/ மாணவருக்கு மாதத்திற்கு",
  /* TODO_HUMAN_REVIEW */ "pricing.monthly.includes": "அனைத்து 4 பாடங்கள் · மாதாந்திர முன்னேற்ற அறிக்கை · WhatsApp / சமூக பகிர்வு",
  /* TODO_HUMAN_REVIEW */ "pricing.package.title": "முழுத் திட்டம்",
  /* TODO_HUMAN_REVIEW */ "pricing.package.recommended": "பரிந்துரைக்கப்பட்டது",
  "pricing.package.price": "LKR 2,000",
  /* TODO_HUMAN_REVIEW */ "pricing.package.unit": "முழு 3 மாத திட்டத்திற்கு (அக்டோபர்–டிசம்பர்)",
  /* TODO_HUMAN_REVIEW */ "pricing.package.save": "மாதாந்திரத்துடன் ஒப்பிடும்போது LKR 250 சேமிக்கவும்",
  /* TODO_HUMAN_REVIEW */ "pricing.package.includes": "அனைத்து 4 பாடங்கள் · மாதாந்திர முன்னேற்ற அறிக்கை · WhatsApp / சமூக பகிர்வு",
  /* TODO_HUMAN_REVIEW */ "pricing.cta.trial": "இலவச சோதனையைத் தொடங்கவும்",

  /* ---- Registration form (Week 3, TODO_HUMAN_REVIEW throughout) ---- */
  /* TODO_HUMAN_REVIEW */ "reg.title": "உங்கள் 7 நாள் இலவச சோதனையைத் தொடங்கவும்",
  /* TODO_HUMAN_REVIEW */ "reg.subtitle": "அட்டை தேவையில்லை. முதல் பாடம் இன்று WhatsApp வழியாக அனுப்பப்படும்.",
  /* TODO_HUMAN_REVIEW */ "reg.parent.name": "பெற்றோரின் பெயர்",
  /* TODO_HUMAN_REVIEW */ "reg.parent.whatsapp": "பெற்றோரின் WhatsApp எண்",
  /* TODO_HUMAN_REVIEW */ "reg.parent.whatsapp.hint": "+94 க்குப் பிறகு 9 இலக்கங்கள் (எ.கா. 771234567)",
  /* TODO_HUMAN_REVIEW */ "reg.student.name": "மாணவரின் பெயர்",
  /* TODO_HUMAN_REVIEW */ "reg.student.grade": "மாணவரின் தற்போதைய வகுப்பு",
  /* TODO_HUMAN_REVIEW */ "reg.student.grade.g5": "5-ம் வகுப்பு (6-ம் வகுப்பிற்கான தயாரிப்பு)",
  /* TODO_HUMAN_REVIEW */ "reg.student.grade.g6": "6-ம் வகுப்பு (இப்போது தொடங்கியது, வலுப்படுத்தல் தேவை)",
  /* TODO_HUMAN_REVIEW */ "reg.student.school": "மாணவரின் பள்ளி (விருப்பம்)",
  /* TODO_HUMAN_REVIEW */ "reg.student.district": "மாவட்டம் / நகரம் (விருப்பம்)",
  /* TODO_HUMAN_REVIEW */ "reg.language": "கற்பித்தல் மொழி",
  /* TODO_HUMAN_REVIEW */ "reg.language.si": "சிங்களம்",
  /* TODO_HUMAN_REVIEW */ "reg.language.ta": "தமிழ்",
  /* TODO_HUMAN_REVIEW */ "reg.language.en": "ஆங்கிலம்",
  /* TODO_HUMAN_REVIEW */ "reg.contact": "விருப்பமான தொடர்பு முறை",
  /* TODO_HUMAN_REVIEW */ "reg.contact.whatsapp": "WhatsApp (பரிந்துரைக்கப்பட்டது)",
  /* TODO_HUMAN_REVIEW */ "reg.contact.phone": "தொலைபேசி அழைப்பு",
  /* TODO_HUMAN_REVIEW */ "reg.contact.email": "மின்னஞ்சல்",
  /* TODO_HUMAN_REVIEW */ "reg.email": "மின்னஞ்சல் (விருப்பம்)",
  /* TODO_HUMAN_REVIEW */ "reg.submit": "இலவச சோதனையைத் தொடங்கவும்",
  /* TODO_HUMAN_REVIEW */ "reg.submitting": "உங்கள் சோதனையை உருவாக்குகிறது…",
  /* TODO_HUMAN_REVIEW */ "reg.success": "வரவேற்கிறோம்! உங்கள் 7 நாள் இலவச சோதனை தொடங்கிவிட்டது. இன்று முதல் பாடத்தை WhatsApp வழியாக அனுப்புவோம்.",
  /* TODO_HUMAN_REVIEW */ "reg.error.generic": "ஏதோ தவறு நடந்தது. மீண்டும் முயற்சிக்கவும் அல்லது +94 77 123 4567 என்ற எண்ணில் WhatsApp செய்யவும்.",
  /* TODO_HUMAN_REVIEW */ "reg.error.whatsapp": "+94 க்குப் பிறகு சரியான 10 இலக்க WhatsApp எண்ணை உள்ளிடவும்.",
  /* TODO_HUMAN_REVIEW */ "reg.have.account": "ஏற்கனவே கணக்கு உள்ளதா?",
  /* TODO_HUMAN_REVIEW */ "reg.login.link": "உள்நுழையவும்",
  "features.algorithms": "வழிமுறைகள்",
  "features.algorithms.desc": "உடல் செயல்பாடுகள் மூலம் வரிசைப்படுத்துதல், தேடுதல் மற்றும் சிக்கல் தீர்த்தல்",
  "features.ai": "AI & ML",
  "features.ai.desc": "மாதிரி அங்கீகாரம், முடிவு மரங்கள் மற்றும் நரம்பு வலையமைப்புகள்",
  "features.data": "தரவு & தர்க்கம்",
  "features.data.desc": "பைனரி, லாஜிக் கேட்ஸ், மறையாக்கம் மற்றும் சுருக்கம்",
  "pricing.simple": "எளிய விலை நிர்ணயம்",
  "welcome": "வரவேற்கிறோம்",
  "ready": "புதிதாக ஏதாவது கற்க தயாரா?",
  "stats.activities": "செயல்பாடுகள்",
  "stats.points": "புள்ளிகள்",
  "stats.time": "செலவழித்த நேரம்",
  "stats.badges": "பேட்ஜ்கள்",
  "congrats": "வாழ்த்துக்கள்!",
  "certificate.download": "சான்றிதழ் பதிவிறக்கம்",
  "activity.start": "செயல்பாட்டைத் தொடங்கு",
  "activity.complete": "முடிந்ததாகக் குறி",
  "activity.instructions": "வழிமுறைகள்",
  "activity.objectives": "கற்றல் நோக்கங்கள்",
  "activity.completed": "செயல்பாடு முடிந்தது!",
  "activity.back": "செயல்பாடுகளுக்குத் திரும்பு",
  "filter.all": "அனைத்தும்",
  "filter.mylevel": "என் நிலை",
  "auth.create": "கணக்கை உருவாக்கு",
  "auth.welcome": "மீண்டும் வரவேற்கிறோம்",
  "auth.fullname": "முழு பெயர்",
  "auth.email": "மின்னஞ்சல்",
  "auth.password": "கடவுச்சொல்",
  "auth.role": "நான் ஒரு",
  "auth.student": "மாணவர்",
  "auth.parent": "பெற்றோர்",
  "auth.teacher": "ஆசிரியர்",
  "auth.loginsubtitle": "தொடர உள்நுழையவும்",
  "auth.startjourney": "உங்கள் கற்றல் பயணத்தைத் தொடங்குங்கள்",
  "auth.noaccount": "கணக்கு இல்லையா?",
  "auth.haveaccount": "ஏற்கனவே கணக்கு உள்ளதா?",
  "pricing.title": "உங்கள் திட்டத்தைத் தேர்ந்தெடுக்கவும்",
  "pricing.subtitle": "அனைத்து பிரீமியம் செயல்பாடுகளையும் அம்சங்களையும் திறக்கவும்",
  "pricing.subscribe": "இப்போது சந்தா செலுத்துங்கள்",
  "pricing.getstarted": "தொடங்குங்கள்",
  "pricing.back": "பின்",
  "pricing.foundation": "அடிப்படை நிலை",
  "pricing.development": "வளர்ச்சி நிலை",
  "pricing.mastery": "தேர்ச்சி நிலை",
  "pricing.methods": "கிடைக்கும் கட்டண முறைகள்",
  "pricing.whatsincluded": "பிரீமியத்தில் என்ன அடங்கும்?",
  "payment.select": "கட்டண முறையைத் தேர்ந்தெடுக்கவும்",
  "payment.qr": "வங்கி பரிமாற்றம் (QR குறியீடு)",
  "payment.qr.desc": "உங்கள் வங்கி செயலியில் QR குறியீட்டை ஸ்கேன் செய்யுங்கள்",
  "payment.card": "அட்டை கட்டணம் (Visa / MasterCard)",
  "payment.card.desc": "உங்கள் கிரெடிட் அல்லது டெபிட் அட்டையில் பாதுகாப்பாக செலுத்துங்கள்",
  "payment.paypal": "பேபால்",
  "payment.paypal.desc": "உங்கள் பேபால் கணக்கு அல்லது அட்டை மூலம் செலுத்துங்கள்",
  "payment.amount": "செலுத்த வேண்டிய தொகை",
  "payment.done": "கட்டணம் செலுத்திவிட்டேன்",
  "payment.instructions": "வழிமுறைகள்",
  "payment.backtomethods": "கட்டண முறைகளுக்குத் திரும்பு",
  "payment.banktransfer": "QR குறியீடு வழியாக வங்கி பரிமாற்றம்",
  "subscription.success": "கட்டணம் வெற்றிகரமாக!",
  "subscription.gotodashboard": "டாஷ்போர்டுக்குச் செல்",
  "loading": "ஏற்றுகிறது...",
  "back": "பின்",
  "free": "இலவசம்",
  "premium": "பிரீமியம்"
};

// Sinhala
// Translation register (Week 3): STANDARD FORMAL WRITTEN SINHALA — the
// register an educated Sri Lankan reader would expect in a school textbook
// or a bank letter. No colloquial or spoken-Sinhala shortcuts.
const si = {
  "lessons.en_only_notice": 'පාඩම් ඉංග්\u200dරීසි භාෂාවෙන් ඉදිරිපත් කෙරේ — ඔබේ ප්\u200dරගතිය සහ සහාය ඔබේ භාෂාවෙන්.',
  "reg.pay.twl.reassurance": "TEC Sri Lanka Worldwide අපගේ මාතෘ සමාගමයි. Genius One Plus එහි වැඩසටහන් වලින් එකකි. ඔබගේ බැංකු රිසිට්පතේ \"TEC SRI LANKA WORLDWIDE (PVT) LTD\" ලෙස ලැබෙන්නා පෙන්වයි — මෙය අප වෙනුවෙනුයි.",
  "home": "මුල් පිටුව",
  "dashboard": "උපකරණ පුවරුව",
  "activities": "ක්‍රියාකාරකම්",
  "progress": "ප්‍රගතිය",
  "logout": "ඉවත් වන්න",
  "login": "ඇතුළු වන්න",
  "register": "ලියාපදිංචි වන්න",
  "hero.title": "ජීනියස් වන් ප්ලස්",
  /* TODO_HUMAN_REVIEW */ "hero.subtitle": "6 වන ශ්‍රේණියට සූදානම් වන්න: ගණිතය, ඉංග්‍රීසි, විද්‍යාව සහ මූලික පරිගණක — ඔබේ භාෂාවෙන්, ඔබේ දුරකථනයෙන්.",
  /* TODO_HUMAN_REVIEW */ "hero.cta": "නොමිලේ පරීක්ෂණය ආරම්භ කරන්න",
  /* TODO_HUMAN_REVIEW */ "hero.cta.secondary": "නියැදි පාඩමක් බලන්න",
  /* TODO_HUMAN_REVIEW */ "hero.trial.badge": "දින 7ක නොමිලේ පරීක්ෂණයක්. කාඩ්පතක් අවශ්‍ය නැත.",
  "hero.tagline": "ශ්‍රී ලංකා පවුල් වෙනුවෙන් 6 ශ්‍රේණි සංක්‍රාන්ති වැඩසටහන",
  /* TODO_HUMAN_REVIEW */ "subjects.section.title": "විෂයයන් හතරක්. එක් සංක්‍රාන්තියක්. සති දොළහක්.",
  /* TODO_HUMAN_REVIEW */ "subjects.maths.title": "ගණිතය",
  /* TODO_HUMAN_REVIEW */ "subjects.maths.subtitle": "පළමු දිනයේ සිටම 6 ශ්‍රේණි ගණිතය ප්‍රගුණ කරන්න",
  /* TODO_HUMAN_REVIEW */ "subjects.maths.body": "භාග සිට ජ්‍යාමිතිය දක්වා — ඔබේ දරුවාට 6 ශ්‍රේණියේ සාර්ථක වීමට අවශ්‍ය පදනම් සකසන්න. සතිපතා ප්‍රශ්න, මාසික ප්‍රගති නිරීක්ෂණය, විනාඩි 15කින් ප්‍රතිඵල.",
  /* TODO_HUMAN_REVIEW */ "subjects.english.title": "ඉංග්‍රීසි",
  /* TODO_HUMAN_REVIEW */ "subjects.english.subtitle": "විශ්වාසයෙන් කියවීම, ලිවීම සහ කථා කිරීම",
  /* TODO_HUMAN_REVIEW */ "subjects.english.body": "6 ශ්‍රේණියේ දී කියවීමේ අවබෝධය, රචනා සහ ව්‍යාකරණ හඳුන්වා දෙයි. පළමු දිනයේ සිටම ඔබේ දරුවා 6 ශ්‍රේණි ඉංග්‍රීසි විෂයට යෝග්‍ය මට්ටමින් සූදානම් වී සිටී.",
  /* TODO_HUMAN_REVIEW */ "subjects.science.title": "විද්‍යාව",
  /* TODO_HUMAN_REVIEW */ "subjects.science.subtitle": "6 ශ්‍රේණි විද්‍යාව සරලව",
  /* TODO_HUMAN_REVIEW */ "subjects.science.body": "ජීව විද්‍යාව, භෞතික විද්‍යාව, රසායන විද්‍යාව — 6 ශ්‍රේණියේදී ඔබේ දරුවා පළමු වරට හමුවන විද්‍යාවන් තුනක්. දැන් මූලික කරුණු ඉගෙන ගෙන, වචන මාලාව හඳුනාගෙන 6 ශ්‍රේණියට පිවිසෙන්න.",
  /* TODO_HUMAN_REVIEW */ "subjects.scratch.title": "Scratch සමඟ මූලික පරිගණක",
  /* TODO_HUMAN_REVIEW */ "subjects.scratch.subtitle": "ඔබේ දරුවාගේ පළමු පරිගණක වැඩසටහන",
  /* TODO_HUMAN_REVIEW */ "subjects.scratch.body": "ලෝකයේ දරුවන් සඳහා වඩාත් ජනප්‍රිය ක්‍රමලේඛන මෙවලම. ඕනෑම ස්මාර්ට්ෆෝන් හෝ ටැබ්ලට් එකක ධාවනය වේ — ලැප්ටොප් අවශ්‍ය නැත. පරිගණක විද්‍යාඥයෙකු මෙන් සිතීමට ඉගෙන ගන්නා අතරම ඔබේ දරුවා සැබෑ ක්‍රීඩා සහ සජීවිකරණ නිර්මාණය කරයි.",
  /* TODO_HUMAN_REVIEW */ "subjects.scratch.badge": "අලුත්",
  /* TODO_HUMAN_REVIEW */ "pricing.section.title": "සරල මිල ගණන්. දින 7ක නොමිලේ පරීක්ෂණය.",
  /* TODO_HUMAN_REVIEW */ "pricing.monthly.title": "මාසික",
  "pricing.monthly.price": "LKR 750",
  /* TODO_HUMAN_REVIEW */ "pricing.monthly.unit": "/ මාසිකව ශිෂ්‍යයෙකු සඳහා",
  /* TODO_HUMAN_REVIEW */ "pricing.monthly.includes": "සියලුම විෂයයන් 4 · මාසික ප්‍රගති වාර්තාව · WhatsApp / සමාජ බෙදාගැනීම",
  /* TODO_HUMAN_REVIEW */ "pricing.package.title": "සම්පූර්ණ වැඩසටහන",
  /* TODO_HUMAN_REVIEW */ "pricing.package.recommended": "නිර්දේශිතයි",
  "pricing.package.price": "LKR 2,000",
  /* TODO_HUMAN_REVIEW */ "pricing.package.unit": "සම්පූර්ණ මාස 3ක වැඩසටහන සඳහා (ඔක්තෝබර්–දෙසැම්බර්)",
  /* TODO_HUMAN_REVIEW */ "pricing.package.save": "මාසිකයට සාපේක්ෂව LKR 250ක් ඉතිරි කරන්න",
  /* TODO_HUMAN_REVIEW */ "pricing.package.includes": "සියලුම විෂයයන් 4 · මාසික ප්‍රගති වාර්තාව · WhatsApp / සමාජ බෙදාගැනීම",
  /* TODO_HUMAN_REVIEW */ "pricing.cta.trial": "නොමිලේ පරීක්ෂණය ආරම්භ කරන්න",

  /* ---- Registration form (Week 3, TODO_HUMAN_REVIEW throughout) ---- */
  /* TODO_HUMAN_REVIEW */ "reg.title": "ඔබේ දින 7 නොමිලේ පරීක්ෂණය ආරම්භ කරන්න",
  /* TODO_HUMAN_REVIEW */ "reg.subtitle": "කාඩ්පතක් අවශ්‍ය නැත. පළමු පාඩම අද WhatsApp හරහා ලැබේ.",
  /* TODO_HUMAN_REVIEW */ "reg.parent.name": "දෙමාපියාගේ නම",
  /* TODO_HUMAN_REVIEW */ "reg.parent.whatsapp": "දෙමාපියාගේ WhatsApp අංකය",
  /* TODO_HUMAN_REVIEW */ "reg.parent.whatsapp.hint": "+94 පසුව අංක 9ක් (උදා: 771234567)",
  /* TODO_HUMAN_REVIEW */ "reg.student.name": "ශිෂ්‍යයාගේ නම",
  /* TODO_HUMAN_REVIEW */ "reg.student.grade": "ශිෂ්‍යයාගේ වර්තමාන ශ්‍රේණිය",
  /* TODO_HUMAN_REVIEW */ "reg.student.grade.g5": "5 ශ්‍රේණිය (6 ශ්‍රේණියට සූදානම් වෙමින්)",
  /* TODO_HUMAN_REVIEW */ "reg.student.grade.g6": "6 ශ්‍රේණිය (දැන් ආරම්භ කළා, ශක්තිමත් කිරීමක් අවශ්‍යයි)",
  /* TODO_HUMAN_REVIEW */ "reg.student.school": "ශිෂ්‍යයාගේ පාසල (විකල්පයි)",
  /* TODO_HUMAN_REVIEW */ "reg.student.district": "දිස්ත්‍රික්කය / නගරය (විකල්පයි)",
  /* TODO_HUMAN_REVIEW */ "reg.language": "ඉගැන්වීමේ භාෂාව",
  /* TODO_HUMAN_REVIEW */ "reg.language.si": "සිංහල",
  /* TODO_HUMAN_REVIEW */ "reg.language.ta": "දෙමළ",
  /* TODO_HUMAN_REVIEW */ "reg.language.en": "ඉංග්‍රීසි",
  /* TODO_HUMAN_REVIEW */ "reg.contact": "කැමති සම්බන්ධතා ක්‍රමය",
  /* TODO_HUMAN_REVIEW */ "reg.contact.whatsapp": "WhatsApp (නිර්දේශිතයි)",
  /* TODO_HUMAN_REVIEW */ "reg.contact.phone": "දුරකථන ඇමතුම",
  /* TODO_HUMAN_REVIEW */ "reg.contact.email": "ඊමේල්",
  /* TODO_HUMAN_REVIEW */ "reg.email": "ඊමේල් (විකල්පයි)",
  /* TODO_HUMAN_REVIEW */ "reg.submit": "නොමිලේ පරීක්ෂණය ආරම්භ කරන්න",
  /* TODO_HUMAN_REVIEW */ "reg.submitting": "ඔබේ පරීක්ෂණය නිර්මාණය වෙමින්…",
  /* TODO_HUMAN_REVIEW */ "reg.success": "සාදරයෙන් පිළිගනිමු! ඔබේ දින 7 නොමිලේ පරීක්ෂණය ආරම්භ වී ඇත. අද පළමු පාඩම WhatsApp හරහා එවන්නෙමු.",
  /* TODO_HUMAN_REVIEW */ "reg.error.generic": "යමක් වැරදී ඇත. නැවත උත්සාහ කරන්න හෝ +94 77 123 4567 ට WhatsApp කරන්න.",
  /* TODO_HUMAN_REVIEW */ "reg.error.whatsapp": "+94 පසුව වලංගු ඉලක්කම් 10ක WhatsApp අංකයක් ඇතුළත් කරන්න.",
  /* TODO_HUMAN_REVIEW */ "reg.have.account": "දැනටමත් ගිණුමක් තිබේද?",
  /* TODO_HUMAN_REVIEW */ "reg.login.link": "පිවිසෙන්න",
  "features.algorithms": "ඇල්ගොරිතම",
  "features.algorithms.desc": "ශාරීරික ක්‍රියාකාරකම් හරහා වර්ග කිරීම, සෙවීම සහ ගැටලු විසඳීම",
  "features.ai": "AI සහ ML",
  "features.ai.desc": "රටා හඳුනාගැනීම, තීරණ ගස් සහ ස්නායු ජාල",
  "features.data": "දත්ත සහ තර්කය",
  "features.data.desc": "ද්විමය, තර්ක ද්වාර, සංකේතනය සහ සම්පීඩනය",
  "pricing.simple": "සරල මිල නියම",
  "welcome": "සාදරයෙන් පිළිගනිමු",
  "ready": "අලුත් දෙයක් ඉගෙන ගන්න සූදානම්ද?",
  "stats.activities": "ක්‍රියාකාරකම්",
  "stats.points": "ලකුණු",
  "stats.time": "ගත කළ කාලය",
  "stats.badges": "ලාංඡන",
  "activity.start": "ක්‍රියාකාරකම ආරම්භ කරන්න",
  "activity.complete": "සම්පූර්ණ යැයි සලකුණු කරන්න",
  "auth.create": "ගිණුමක් සාදන්න",
  "auth.welcome": "නැවත සාදරයෙන් පිළිගනිමු",
  "auth.student": "ශිෂ්‍යයා",
  "auth.parent": "දෙමාපියන්",
  "auth.teacher": "ගුරුවරයා",
  "pricing.title": "ඔබේ සැලැස්ම තෝරන්න",
  "pricing.subtitle": "සියලුම ප්‍රිමියම් ක්‍රියාකාරකම් සහ විශේෂාංග අගුළු හරින්න",
  "pricing.subscribe": "දැන්ම දායක වන්න",
  "pricing.getstarted": "ආරම්භ කරන්න",
  "pricing.foundation": "පදනම් මට්ටම",
  "pricing.development": "සංවර්ධන මට්ටම",
  "pricing.mastery": "ප්‍රවීණතා මට්ටම",
  "pricing.methods": "පවතින ගෙවීම් ක්‍රම",
  "payment.select": "ගෙවීම් ක්‍රමය තෝරන්න",
  "payment.qr": "බැංකු මාරුව (QR කේතය)",
  "payment.card": "කාඩ්පත් ගෙවීම (Visa / MasterCard)",
  "payment.paypal": "පේපෑල්",
  "payment.done": "මම ගෙවීම සිදු කළා",
  "payment.backtomethods": "ගෙවීම් ක්‍රමවලට ආපසු",
  "subscription.success": "ගෙවීම සාර්ථකයි!",
  "subscription.gotodashboard": "උපකරණ පුවරුවට යන්න",
  "loading": "පූරණය වෙමින්...",
  "back": "ආපසු",
  "free": "නොමිලේ",
  "premium": "ප්‍රිමියම්"
};

// Hindi
const hi = {
  "lessons.en_only_notice": 'पाठ अंग्रेज़ी में दिए जाते हैं — आपकी प्रगति और सहायता आपकी भाषा में।',
  // Sprint 8 — Genius One Plus additions (Hindi) /* TODO_HUMAN_REVIEW */
  "hero.tagline": "कक्षा 5 से कक्षा 6 तैयारी — गणित, अंग्रेज़ी, विज्ञान और Scratch",
  "hero.trial.badge": "🎉 अक्टूबर मुफ्त — कोई भुगतान नहीं",
  "hero.illustration.caption": "हर बच्चा एक सवाल से शुरू होता है। जीनियस वन प्लस सवालों को कौशल में बदलता है।",
  "pricing.section.title": "आसान क़ीमत",
  "pricing.monthly.price": "LKR 750",
  "pricing.package.price": "LKR 2,000",
  "pricing.package.save": "मासिक से LKR 250 बचाएं",
  "reg.success": "स्वागत है! आज हम आपको पहला पाठ WhatsApp पर भेजेंगे।",
  "reg.free.title": "🎉 इस अक्टूबर आप मुफ्त में शामिल हैं!",
  "reg.pay.title": "एक कदम बाकी — Bank of Ceylon ट्रांसफर",
  "lessons.title": "पाठ",
  "lessons.subtitle": "एक पाठ चुनें, क्विज़ पूरा करें, अपनी प्रगति देखें।",
  "quiz.submit": "क्विज़ जमा करें",
  "progress.stat.streak": "लगातार दिन",
  "gallery.section.title": "हमारी दृष्टि, चित्रों में",
  // Sprint 8.5 — landing feature cards (GPT-4o auto-translation, needs native review)
  "subjects.section.title": "चार विषय। एक बदलाव। बारह हफ्ते।",
  "subjects.maths.title": "गणित",
  "subjects.maths.subtitle": "पहले दिन से कक्षा 6 गणित में महारत",
  "subjects.maths.body": "भिन्न से लेकर ज्यामिति तक — अपने बच्चे के लिए कक्षा 6 की सफलता की नींव बनाएं। साप्ताहिक अभ्यास प्रश्न, मासिक प्रगति ट्रैकिंग, 15 मिनट में परिणाम।",
  "subjects.english.title": "अंग्रेज़ी",
  "subjects.english.subtitle": "आत्मविश्वास से पढ़ें, लिखें और बोलें",
  "subjects.english.body": "कक्षा 6 में पढ़ाई की समझ, निबंध और व्याकरण शामिल होते हैं। हमारे पाठ आपके बच्चे को पहले दिन से ही कक्षा 6 अंग्रेज़ी में ग्रेड स्तर पर प्रवेश करने के लिए तैयार करते हैं।",
  "subjects.science.title": "विज्ञान",
  "subjects.science.subtitle": "कक्षा 6 विज्ञान को सरल बनाएं",
  "subjects.science.body": "जीवविज्ञान, भौतिकी, रसायन विज्ञान — तीन विज्ञान जो आपके बच्चे को पहली बार कक्षा 6 में मिलते हैं। अब से मूल बातें शुरू करें, कक्षा 6 में पहले से ही शब्दावली जानकर प्रवेश करें।",
  "subjects.scratch.title": "Scratch के साथ बुनियादी कंप्यूटिंग",
  "subjects.scratch.subtitle": "आपके बच्चे का पहला कंप्यूटर प्रोग्राम",
  "subjects.scratch.body": "बच्चों के लिए दुनिया का सबसे लोकप्रिय प्रोग्रामिंग टूल। किसी भी स्मार्टफोन या टैबलेट पर चलता है — लैपटॉप की जरूरत नहीं। आपका बच्चा असली गेम और एनिमेशन बनाता है और कंप्यूटर वैज्ञानिक की तरह सोचना सीखता है।",
  "subjects.scratch.badge": "NEW",
  // Sprint 8.5 — Bangladesh preview link (visible in bn mode; harmless in hi)
  "bd.preview.link": "बांग्लादेश में जल्द ही →",
  "home": "होम",
  "dashboard": "डैशबोर्ड",
  "activities": "गतिविधियाँ",
  "progress": "प्रगति",
  "logout": "लॉग आउट",
  "login": "लॉगिन",
  "register": "पंजीकरण करें",
  "hero.title": "फ्यूचरिस्टिक ग्लोबल AI अकादमी",
  "hero.subtitle": "13 भाषाओं में नवीन गतिविधियों के माध्यम से AI और कंप्यूटर विज्ञान सीखें",
  "hero.cta": "मुफ्त में सीखना शुरू करें",
  // Sprint 8 — `hero.tagline` is defined at the top of this block with the
  // Genius One Plus wording; the legacy AI-Academy override was removed.
  "features.algorithms": "एल्गोरिदम",
  "features.algorithms.desc": "शारीरिक गतिविधियों के माध्यम से छंटाई, खोज और समस्या-समाधान",
  "features.ai": "AI और ML",
  "features.ai.desc": "पैटर्न पहचान, निर्णय वृक्ष और तंत्रिका नेटवर्क",
  "features.data": "डेटा और तर्क",
  "features.data.desc": "बाइनरी, लॉजिक गेट्स, एन्क्रिप्शन और कंप्रेशन",
  "pricing.simple": "सरल मूल्य निर्धारण",
  "welcome": "स्वागत है",
  "ready": "कुछ नया सीखने के लिए तैयार?",
  "stats.activities": "गतिविधियाँ",
  "stats.points": "अंक",
  "stats.time": "बिताया गया समय",
  "stats.badges": "बैज",
  "activity.start": "गतिविधि शुरू करें",
  "activity.complete": "पूर्ण के रूप में चिह्नित करें",
  "auth.create": "खाता बनाएं",
  "auth.welcome": "वापस स्वागत है",
  "auth.student": "छात्र",
  "auth.parent": "अभिभावक",
  "auth.teacher": "शिक्षक",
  "pricing.title": "अपना प्लान चुनें",
  "pricing.subtitle": "सभी प्रीमियम गतिविधियों और सुविधाओं को अनलॉक करें",
  "pricing.subscribe": "अभी सदस्यता लें",
  "pricing.getstarted": "शुरू करें",
  "pricing.foundation": "फाउंडेशन स्तर",
  "pricing.development": "विकास स्तर",
  "pricing.mastery": "दक्षता स्तर",
  "pricing.methods": "उपलब्ध भुगतान विधियाँ",
  "payment.select": "भुगतान विधि चुनें",
  "payment.qr": "बैंक ट्रांसफर (QR कोड)",
  "payment.card": "कार्ड भुगतान (Visa / MasterCard)",
  "payment.paypal": "पेपैल",
  "payment.done": "मैंने भुगतान कर दिया है",
  "payment.backtomethods": "भुगतान विधियों पर वापस",
  "subscription.success": "भुगतान सफल!",
  "subscription.gotodashboard": "डैशबोर्ड पर जाएं",
  "loading": "लोड हो रहा है...",
  "back": "पीछे",
  "free": "मुफ्त",
  "premium": "प्रीमियम"
};

// Malay
const ms = {
  "lessons.en_only_notice": 'Pelajaran disampaikan dalam bahasa Inggeris — kemajuan dan sokongan anda dalam bahasa anda.',
  // Sprint 8 — Genius One Plus additions (Malay) /* TODO_HUMAN_REVIEW */
  "hero.tagline": "Program peralihan Darjah 5 → Darjah 6 — Matematik, Bahasa Inggeris, Sains & Scratch",
  "hero.trial.badge": "🎉 Oktober percuma — tiada bayaran",
  "hero.illustration.caption": "Setiap kanak-kanak bermula dengan soalan. Genius One Plus mengubah soalan menjadi kemahiran.",
  "pricing.section.title": "Harga yang mudah",
  "pricing.monthly.price": "LKR 750",
  "pricing.package.price": "LKR 2,000",
  "pricing.package.save": "Jimat LKR 250 berbanding bulanan",
  "reg.success": "Selamat datang! Kami akan hantar pelajaran pertama melalui WhatsApp hari ini.",
  "reg.free.title": "🎉 Anda diterima PERCUMA untuk Oktober ini!",
  "reg.pay.title": "Satu langkah lagi — Pindahan Bank of Ceylon",
  "lessons.title": "Pelajaran",
  "lessons.subtitle": "Pilih pelajaran, selesaikan kuiz, lihat kemajuan anda.",
  "quiz.submit": "Hantar kuiz",
  "progress.stat.streak": "Hari berturut-turut",
  "gallery.section.title": "Visi kami, dilukis",
  // Sprint 8.5 — landing feature cards (GPT-4o auto-translation, needs native review)
  "subjects.section.title": "Empat subjek. Satu peralihan. Dua belas minggu.",
  "subjects.maths.title": "Matematik",
  "subjects.maths.subtitle": "Kuasai Matematik Darjah 6 dari Hari Pertama",
  "subjects.maths.body": "Daripada pecahan kepada geometri — bina asas yang diperlukan anak anda untuk kejayaan Darjah 6. Soalan latihan mingguan, penjejakan kemajuan bulanan, keputusan dalam 15 minit.",
  "subjects.english.title": "Bahasa Inggeris",
  "subjects.english.subtitle": "Baca, Tulis, dan Bercakap dengan Yakin",
  "subjects.english.body": "Darjah 6 memperkenalkan kefahaman bacaan, esei, dan tatabahasa. Pelajaran kami mempersiapkan anak anda untuk memasuki Bahasa Inggeris Darjah 6 pada tahap gred dari hari pertama.",
  "subjects.science.title": "Sains",
  "subjects.science.subtitle": "Sains Darjah 6 Jadi Mudah",
  "subjects.science.body": "Biologi, fizik, kimia — tiga sains yang anak anda temui buat kali pertama dalam Darjah 6. Mulakan dengan asas sekarang, masuk ke Darjah 6 sudah mengetahui perbendaharaan kata.",
  "subjects.scratch.title": "Pengkomputeran Asas dengan Scratch",
  "subjects.scratch.subtitle": "Program Komputer Pertama Anak Anda",
  "subjects.scratch.body": "Alat pengaturcaraan paling popular di dunia untuk kanak-kanak. Berfungsi pada mana-mana telefon pintar atau tablet — tiada komputer riba diperlukan. Anak anda membina permainan dan animasi sebenar sambil belajar berfikir seperti seorang saintis komputer.",
  "subjects.scratch.badge": "BARU",
  "bd.preview.link": "Akan datang ke Bangladesh →",
  "home": "Laman Utama",
  "dashboard": "Papan Pemuka",
  "activities": "Aktiviti",
  "progress": "Kemajuan",
  "logout": "Log Keluar",
  "login": "Log Masuk",
  "register": "Daftar",
  "hero.title": "Akademi AI Global Futuristik",
  "hero.subtitle": "Belajar AI & Sains Komputer melalui aktiviti hands-on yang menyeronokkan dalam 13 bahasa",
  "hero.cta": "Mula Belajar Percuma",
  // Sprint 8 — Genius One Plus `hero.tagline` at the top of this block wins;
  // legacy AI-Academy override removed.
  "features.algorithms": "Algoritma",
  "features.algorithms.desc": "Pengisihan, pencarian dan penyelesaian masalah melalui aktiviti fizikal",
  "features.ai": "AI & ML",
  "features.ai.desc": "Pengecaman corak, pokok keputusan dan rangkaian neural",
  "features.data": "Data & Logik",
  "features.data.desc": "Binari, get logik, penyulitan dan pemampatan",
  "pricing.simple": "Harga Mudah",
  "welcome": "Selamat Datang",
  "ready": "Bersedia untuk belajar sesuatu yang baru?",
  "stats.activities": "Aktiviti",
  "stats.points": "Mata",
  "stats.time": "Masa Dihabiskan",
  "stats.badges": "Lencana",
  "auth.create": "Cipta Akaun",
  "auth.welcome": "Selamat Kembali",
  "pricing.title": "Pilih Pelan Anda",
  "pricing.subscribe": "Langgan Sekarang",
  "pricing.getstarted": "Mula",
  "payment.select": "Pilih Kaedah Pembayaran",
  "payment.done": "Saya Telah Membuat Pembayaran",
  "loading": "Memuat...",
  "back": "Kembali",
  "free": "Percuma",
  "premium": "Premium"
};

// Filipino
const tl = {
  "home": "Tahanan",
  "dashboard": "Dashboard",
  "activities": "Mga Aktibidad",
  "progress": "Pag-unlad",
  "logout": "Mag-logout",
  "login": "Mag-login",
  "register": "Magrehistro",
  "hero.title": "Futuristic Global AI Academy",
  "hero.subtitle": "Matuto ng AI at Computer Science sa pamamagitan ng masayang hands-on na mga aktibidad sa 13 wika",
  "hero.cta": "Magsimulang Matuto Nang Libre",
  "hero.tagline": "Unplugged AI Academy - Mag-compute Gamit ang Iyong Utak",
  "features.algorithms": "Mga Algorithm",
  "features.algorithms.desc": "Pag-uuri, paghahanap at paglutas ng problema sa pamamagitan ng pisikal na aktibidad",
  "features.ai": "AI at ML",
  "features.ai.desc": "Pagkilala ng pattern, decision trees at neural networks",
  "features.data": "Data at Lohika",
  "features.data.desc": "Binary, logic gates, encryption at compression",
  "pricing.simple": "Simpleng Presyo",
  "welcome": "Maligayang pagdating",
  "ready": "Handa ka na bang matuto ng bagong bagay?",
  "stats.activities": "Mga Aktibidad",
  "stats.points": "Puntos",
  "stats.time": "Oras na Ginugol",
  "auth.create": "Gumawa ng Account",
  "auth.welcome": "Maligayang Pagbabalik",
  "pricing.title": "Pumili ng Plano",
  "pricing.subscribe": "Mag-subscribe Ngayon",
  "pricing.getstarted": "Magsimula",
  "payment.select": "Pumili ng Paraan ng Pagbabayad",
  "payment.done": "Nagbayad Na Ako",
  "loading": "Naglo-load...",
  "back": "Bumalik",
  "free": "Libre",
  "premium": "Premium"
};

// Chinese Simplified
const zh = {
  "home": "首页",
  "dashboard": "仪表板",
  "activities": "活动",
  "progress": "进度",
  "logout": "登出",
  "login": "登录",
  "register": "注册",
  "hero.title": "未来全球AI学院",
  "hero.subtitle": "通过13种语言的创新实践活动学习AI和计算机科学",
  "hero.cta": "免费开始学习",
  "hero.tagline": "无插件AI学院 - 用你的大脑计算",
  "features.algorithms": "算法",
  "features.algorithms.desc": "通过物理活动进行排序、搜索和问题解决",
  "features.ai": "人工智能与机器学习",
  "features.ai.desc": "模式识别、决策树和神经网络",
  "features.data": "数据与逻辑",
  "features.data.desc": "二进制、逻辑门、加密和压缩",
  "pricing.simple": "简单定价",
  "welcome": "欢迎",
  "ready": "准备学习新东西了吗？",
  "stats.activities": "活动",
  "stats.points": "积分",
  "stats.time": "花费时间",
  "stats.badges": "徽章",
  "auth.create": "创建账户",
  "auth.welcome": "欢迎回来",
  "pricing.title": "选择您的计划",
  "pricing.subscribe": "立即订阅",
  "pricing.getstarted": "开始",
  "pricing.foundation": "基础级别",
  "pricing.development": "发展级别",
  "pricing.mastery": "精通级别",
  "payment.select": "选择付款方式",
  "payment.done": "我已完成付款",
  "loading": "加载中...",
  "back": "返回",
  "free": "免费",
  "premium": "高级"
};

// Thai
const th = {
  "home": "หน้าแรก",
  "dashboard": "แดชบอร์ด",
  "activities": "กิจกรรม",
  "progress": "ความคืบหน้า",
  "logout": "ออกจากระบบ",
  "login": "เข้าสู่ระบบ",
  "register": "ลงทะเบียน",
  "hero.title": "สถาบัน AI ระดับโลกแห่งอนาคต",
  "hero.subtitle": "เรียนรู้ AI และวิทยาการคอมพิวเตอร์ผ่านกิจกรรมปฏิบัติจริงใน 13 ภาษา",
  "hero.cta": "เริ่มเรียนฟรี",
  "hero.tagline": "Unplugged AI Academy - คำนวณด้วยสมองของคุณ",
  "features.algorithms": "อัลกอริทึม",
  "features.algorithms.desc": "การเรียงลำดับ การค้นหา และการแก้ปัญหาผ่านกิจกรรมทางกายภาพ",
  "features.ai": "AI และ ML",
  "features.ai.desc": "การจดจำรูปแบบ ต้นไม้ตัดสินใจ และเครือข่ายประสาท",
  "features.data": "ข้อมูลและตรรกะ",
  "features.data.desc": "ไบนารี ลอจิกเกต การเข้ารหัส และการบีบอัด",
  "pricing.simple": "ราคาเรียบง่าย",
  "welcome": "ยินดีต้อนรับ",
  "ready": "พร้อมที่จะเรียนรู้สิ่งใหม่แล้วหรือยัง?",
  "stats.activities": "กิจกรรม",
  "stats.points": "คะแนน",
  "auth.create": "สร้างบัญชี",
  "auth.welcome": "ยินดีต้อนรับกลับ",
  "pricing.title": "เลือกแผนของคุณ",
  "pricing.subscribe": "สมัครสมาชิกตอนนี้",
  "pricing.getstarted": "เริ่มต้น",
  "payment.select": "เลือกวิธีการชำระเงิน",
  "payment.done": "ฉันชำระเงินแล้ว",
  "loading": "กำลังโหลด...",
  "back": "กลับ",
  "free": "ฟรี",
  "premium": "พรีเมียม"
};

// Urdu
const ur = {
  "home": "ہوم",
  "dashboard": "ڈیش بورڈ",
  "activities": "سرگرمیاں",
  "progress": "پیش رفت",
  "logout": "لاگ آؤٹ",
  "login": "لاگ ان",
  "register": "رجسٹر کریں",
  "hero.title": "فیوچرسٹک گلوبل AI اکیڈمی",
  "hero.subtitle": "13 زبانوں میں اختراعی سرگرمیوں کے ذریعے AI اور کمپیوٹر سائنس سیکھیں",
  "hero.cta": "مفت سیکھنا شروع کریں",
  "hero.tagline": "ان پلگڈ AI اکیڈمی - اپنے دماغ سے حساب لگائیں",
  "features.algorithms": "الگورتھم",
  "features.algorithms.desc": "جسمانی سرگرمیوں کے ذریعے ترتیب، تلاش اور مسئلہ حل کرنا",
  "features.ai": "AI اور ML",
  "features.ai.desc": "پیٹرن کی شناخت، فیصلے کے درخت اور نیورل نیٹ ورکس",
  "features.data": "ڈیٹا اور منطق",
  "features.data.desc": "بائنری، لاجک گیٹس، خفیہ کاری اور کمپریشن",
  "pricing.simple": "سادہ قیمت",
  "welcome": "خوش آمدید",
  "ready": "کچھ نیا سیکھنے کے لیے تیار ہیں?",
  "stats.activities": "سرگرمیاں",
  "stats.points": "پوائنٹس",
  "auth.create": "اکاؤنٹ بنائیں",
  "auth.welcome": "واپسی پر خوش آمدید",
  "pricing.title": "اپنا پلان منتخب کریں",
  "pricing.subscribe": "ابھی سبسکرائب کریں",
  "payment.select": "ادائیگی کا طریقہ منتخب کریں",
  "payment.done": "میں نے ادائیگی کر دی ہے",
  "loading": "لوڈ ہو رہا ہے...",
  "back": "واپس",
  "free": "مفت",
  "premium": "پریمیم",
  // Sprint 11 (Task J) — RTL waitlist badge for /pk
  "waitlist.eta.coming_2027": "2027 میں آ رہا ہے"
};

// Bengali
const bn = {
  "lessons.en_only_notice": 'পাঠ ইংরেজিতে প্রদান করা হয় — আপনার অগ্রগতি এবং সহায়তা আপনার ভাষায়।',
  // Sprint 8 — Genius One Plus additions (Bengali) /* TODO_HUMAN_REVIEW */
  "hero.tagline": "ক্লাস ৫ থেকে ক্লাস ৬ প্রস্তুতি — গণিত, ইংরেজি, বিজ্ঞান ও Scratch",
  "hero.trial.badge": "🎉 অক্টোবর বিনামূল্যে — কোনো ফি নেই",
  "hero.illustration.caption": "প্রতিটি শিশু একটি প্রশ্ন দিয়ে শুরু করে। জিনিয়াস ওয়ান প্লাস প্রশ্নকে দক্ষতায় রূপান্তর করে।",
  "pricing.section.title": "সহজ মূল্য",
  "pricing.monthly.price": "LKR 750",
  "pricing.package.price": "LKR 2,000",
  "pricing.package.save": "মাসিকের চেয়ে LKR 250 সাশ্রয়",
  "reg.success": "স্বাগত! আজই আপনার প্রথম পাঠ WhatsApp-এ পাঠানো হবে।",
  "reg.free.title": "🎉 এই অক্টোবরে আপনি বিনামূল্যে যোগ দিয়েছেন!",
  "reg.pay.title": "শেষ ধাপ — Bank of Ceylon ট্রান্সফার",
  "lessons.title": "পাঠসমূহ",
  "lessons.subtitle": "একটি পাঠ বাছুন, কুইজ সম্পূর্ণ করুন, আপনার অগ্রগতি দেখুন।",
  "quiz.submit": "কুইজ জমা দিন",
  "progress.stat.streak": "ধারাবাহিক দিন",
  "gallery.section.title": "আমাদের দৃষ্টি, চিত্রে",
  // Sprint 8.5 — landing feature cards (GPT-4o auto-translation, needs native review)
  "subjects.section.title": "চারটি বিষয়। একটি পরিবর্তন। বারো সপ্তাহ।",
  "subjects.maths.title": "গণিত",
  "subjects.maths.subtitle": "প্রথম দিন থেকেই ক্লাস ৬ এর গণিত আয়ত্ত করুন",
  "subjects.maths.body": "ভগ্নাংশ থেকে জ্যামিতি — ক্লাস ৬ এ সাফল্যের জন্য আপনার সন্তানের প্রয়োজনীয় ভিত্তি তৈরি করুন। সাপ্তাহিক অনুশীলন প্রশ্ন, মাসিক অগ্রগতি ট্র্যাকিং, ১৫ মিনিটে ফলাফল।",
  "subjects.english.title": "ইংরেজি",
  "subjects.english.subtitle": "আত্মবিশ্বাসের সাথে পড়া, লেখা এবং কথা বলা",
  "subjects.english.body": "ক্লাস ৬ এ পাঠ্যবোধ, প্রবন্ধ এবং ব্যাকরণ পরিচিত হয়। আমাদের পাঠ আপনার সন্তানকে প্রথম দিন থেকেই ক্লাস ৬ এর ইংরেজি স্তরে প্রবেশের জন্য প্রস্তুত করে।",
  "subjects.science.title": "বিজ্ঞান",
  "subjects.science.subtitle": "ক্লাস ৬ এর বিজ্ঞান সহজ করে দেওয়া হয়েছে",
  "subjects.science.body": "জীববিদ্যা, পদার্থবিজ্ঞান, রসায়ন — তিনটি বিজ্ঞান যা আপনার সন্তান প্রথমবারের মতো ক্লাস ৬ এ পরিচিত হয়। এখন থেকেই মৌলিক বিষয়গুলি শুরু করুন, ক্লাস ৬ এ প্রবেশের আগেই শব্দভাণ্ডার জেনে নিন।",
  "subjects.scratch.title": "স্ক্র্যাচ দিয়ে প্রাথমিক কম্পিউটিং",
  "subjects.scratch.subtitle": "আপনার সন্তানের প্রথম কম্পিউটার প্রোগ্রাম",
  "subjects.scratch.body": "বাচ্চাদের জন্য বিশ্বের সবচেয়ে জনপ্রিয় প্রোগ্রামিং টুল। যে কোনো স্মার্টফোন বা ট্যাবলেটে চলে — ল্যাপটপের প্রয়োজন নেই। আপনার সন্তান কম্পিউটার বিজ্ঞানীর মতো চিন্তা করতে শিখতে শিখতে বাস্তব গেম এবং অ্যানিমেশন তৈরি করে।",
  "subjects.scratch.badge": "নতুন",
  "bd.preview.link": "শীঘ্রই বাংলাদেশে আসছে →",
  "bd.badge": "Q3 2027 — বাংলাদেশে আসছে",
  "bd.hero.title": "জিনিয়াস ওয়ান প্লাস বাংলাদেশে আসছে",
  "bd.hero.sub": "ঢাকা • চট্টগ্রাম • সিলেট — জিনিয়াস ওয়ান প্লাস আসছে",
  "bd.waitlist.title": "লঞ্চের সময় আমাকে জানান",
  "bd.waitlist.cta": "আমাকে জানান",
  "bd.waitlist.done": "আপনি তালিকায় আছেন। লঞ্চ হলে আমরা WhatsApp-এ জানাব।",
  "bd.pricing.tba": "৳ (টাকা) মূল্য লঞ্চের কাছাকাছি ঘোষণা করা হবে। এখন কোনো ক্রয় নেই।",
  "home": "হোম",
  "dashboard": "ড্যাশবোর্ড",
  "activities": "কার্যক্রম",
  "progress": "অগ্রগতি",
  "logout": "লগ আউট",
  "login": "লগইন",
  "register": "নিবন্ধন করুন",
  "hero.title": "ফিউচারিস্টিক গ্লোবাল AI একাডেমি",
  "hero.subtitle": "১৩টি ভাষায় উদ্ভাবনী কার্যক্রমের মাধ্যমে AI এবং কম্পিউটার বিজ্ঞান শিখুন",
  "hero.cta": "বিনামূল্যে শেখা শুরু করুন",
  // Sprint 8 — Genius One Plus `hero.tagline` at the top of this block wins;
  // legacy AI-Academy override removed.
  "features.algorithms": "অ্যালগরিদম",
  "features.algorithms.desc": "শারীরিক কার্যক্রমের মাধ্যমে সাজানো, অনুসন্ধান এবং সমস্যা সমাধান",
  "features.ai": "AI এবং ML",
  "features.ai.desc": "প্যাটার্ন চিনতে পারা, ডিসিশন ট্রি এবং নিউরাল নেটওয়ার্ক",
  "features.data": "ডেটা এবং যুক্তি",
  "features.data.desc": "বাইনারি, লজিক গেট, এনক্রিপশন এবং কম্প্রেশন",
  "pricing.simple": "সহজ মূল্য",
  "welcome": "স্বাগতম",
  "ready": "নতুন কিছু শিখতে প্রস্তুত?",
  "stats.activities": "কার্যক্রম",
  "stats.points": "পয়েন্ট",
  "auth.create": "অ্যাকাউন্ট তৈরি করুন",
  "auth.welcome": "আবার স্বাগতম",
  "pricing.title": "আপনার পরিকল্পনা বেছে নিন",
  "pricing.subscribe": "এখনই সদস্যতা নিন",
  "payment.select": "পেমেন্ট পদ্ধতি নির্বাচন করুন",
  "payment.done": "আমি পেমেন্ট করেছি",
  "loading": "লোড হচ্ছে...",
  "back": "ফিরে যান",
  "free": "বিনামূল্যে",
  "premium": "প্রিমিয়াম"
};

// Traditional Chinese
const zhTW = {
  "home": "首頁",
  "dashboard": "儀表板",
  "activities": "活動",
  "progress": "進度",
  "logout": "登出",
  "login": "登入",
  "register": "註冊",
  "hero.title": "未來全球AI學院",
  "hero.subtitle": "通過13種語言的創新實踐活動學習AI和電腦科學",
  "hero.cta": "免費開始學習",
  "hero.tagline": "無插件AI學院 - 用你的大腦計算",
  "features.algorithms": "演算法",
  "features.algorithms.desc": "通過身體活動進行排序、搜索和問題解決",
  "features.ai": "人工智慧與機器學習",
  "features.ai.desc": "模式識別、決策樹和神經網路",
  "features.data": "數據與邏輯",
  "features.data.desc": "二進位、邏輯閘、加密和壓縮",
  "pricing.simple": "簡單定價",
  "welcome": "歡迎",
  "ready": "準備學習新東西了嗎？",
  "stats.activities": "活動",
  "stats.points": "積分",
  "auth.create": "建立帳戶",
  "auth.welcome": "歡迎回來",
  "pricing.title": "選擇您的計劃",
  "pricing.subscribe": "立即訂閱",
  "payment.select": "選擇付款方式",
  "payment.done": "我已完成付款",
  "loading": "載入中...",
  "back": "返回",
  "free": "免費",
  "premium": "高級"
};

// Korean
const ko = {
  "home": "홈",
  "dashboard": "대시보드",
  "activities": "활동",
  "progress": "진행 상황",
  "logout": "로그아웃",
  "login": "로그인",
  "register": "등록",
  "hero.title": "미래형 글로벌 AI 아카데미",
  "hero.subtitle": "13개 언어로 혁신적인 실습 활동을 통해 AI와 컴퓨터 과학을 배우세요",
  "hero.cta": "무료로 학습 시작",
  "hero.tagline": "언플러그드 AI 아카데미 - 두뇌로 계산하기",
  "features.algorithms": "알고리즘",
  "features.algorithms.desc": "물리적 활동을 통한 정렬, 검색 및 문제 해결",
  "features.ai": "AI 및 ML",
  "features.ai.desc": "패턴 인식, 의사 결정 트리 및 신경 네트워크",
  "features.data": "데이터 및 논리",
  "features.data.desc": "이진법, 논리 게이트, 암호화 및 압축",
  "pricing.simple": "간단한 가격",
  "welcome": "환영합니다",
  "ready": "새로운 것을 배울 준비가 되셨나요?",
  "stats.activities": "활동",
  "stats.points": "포인트",
  "auth.create": "계정 만들기",
  "auth.welcome": "다시 오신 것을 환영합니다",
  "pricing.title": "요금제를 선택하세요",
  "pricing.subscribe": "지금 구독",
  "payment.select": "결제 방법 선택",
  "payment.done": "결제를 완료했습니다",
  "loading": "로딩 중...",
  "back": "뒤로",
  "free": "무료",
  "premium": "프리미엄"
};

// Japanese
const ja = {
  "home": "ホーム",
  "dashboard": "ダッシュボード",
  "activities": "アクティビティ",
  "progress": "進捗",
  "logout": "ログアウト",
  "login": "ログイン",
  "register": "登録",
  "hero.title": "未来型グローバルAIアカデミー",
  "hero.subtitle": "13言語で革新的な実践活動を通じてAIとコンピューターサイエンスを学びましょう",
  "hero.cta": "無料で学習を始める",
  "hero.tagline": "アンプラグドAIアカデミー - 脳で計算しよう",
  "features.algorithms": "アルゴリズム",
  "features.algorithms.desc": "身体活動を通じたソート、検索、問題解決",
  "features.ai": "AIとML",
  "features.ai.desc": "パターン認識、決定木、ニューラルネットワーク",
  "features.data": "データとロジック",
  "features.data.desc": "バイナリ、論理ゲート、暗号化、圧縮",
  "pricing.simple": "シンプルな料金",
  "welcome": "ようこそ",
  "ready": "新しいことを学ぶ準備はできていますか？",
  "stats.activities": "アクティビティ",
  "stats.points": "ポイント",
  "auth.create": "アカウントを作成",
  "auth.welcome": "おかえりなさい",
  "pricing.title": "プランを選択してください",
  "pricing.subscribe": "今すぐ購読",
  "payment.select": "支払い方法を選択",
  "payment.done": "支払いを完了しました",
  "loading": "読み込み中...",
  "back": "戻る",
  "free": "無料",
  "premium": "プレミアム"
};


const resources = {
  en: { translation: en },
  ta: { translation: ta },
  si: { translation: si },
  // Bilingual (native + English side-by-side). Aliased to the native
  // translation table — the `bilingual` postProcessor above appends the
  // English string at render time. No separate translation work.
  'si-en-dual': { translation: si },
  'ta-en-dual': { translation: ta },
  hi: { translation: hi },
  ms: { translation: ms },
  tl: { translation: tl },
  zh: { translation: zh },
  th: { translation: th },
  ur: { translation: ur },
  bn: { translation: bn },
  'zh-TW': { translation: zhTW },
  ko: { translation: ko },
  ja: { translation: ja }
};

i18n
  .use(LanguageDetector)
  .use(bilingualPostProcessor)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    postProcess: ['bilingual'],
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

export default i18n;
